import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import type { BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Modal } from '~/components/ui/modal'
import { IndicatorDetail } from '~/features/monitoring/indicator-detail'
import { getDeviations, getOverview } from '~/api/monitoring'
import type { DeviationDetail, OverviewResponse } from '~/api/types'

const VERDICT_TONE: Record<string, BadgeTone> = {
    적합: 'success',
    근접: 'warning',
    이탈: 'danger',
    판정불가: 'neutral',
    기록: 'info',
}

/** 점검 현황 — 체크리스트 정량지표의 최신 측정값을 기준과 대조해 준수 상태를 보여준다. */
export function OverviewSection({ dashboardId }: { dashboardId: string }) {
    const navigate = useNavigate()
    const [data, setData] = useState<OverviewResponse | null>(null)
    const [loaded, setLoaded] = useState(false)
    // 행 클릭 시 모달로 보여줄 지표(fieldId). 지표 상세(차트+평가항목)는 deviations에서 1회 fetch 후 캐시.
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
    const [details, setDetails] = useState<DeviationDetail[] | null>(null)
    const [detailsLoading, setDetailsLoading] = useState(false)

    useEffect(() => {
        getOverview(dashboardId)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoaded(true))
    }, [dashboardId])

    // 대시보드가 바뀌면 모달·캐시를 초기화.
    useEffect(() => {
        setSelectedFieldId(null)
        setDetails(null)
    }, [dashboardId])

    function openIndicator(fieldId: string) {
        setSelectedFieldId(fieldId)
        if (details === null && !detailsLoading) {
            setDetailsLoading(true)
            getDeviations(dashboardId, true)
                .then(setDetails)
                .catch(() => setDetails([]))
                .finally(() => setDetailsLoading(false))
        }
    }

    if (!loaded) {
        return <p className='text-muted-foreground text-sm'>불러오는 중…</p>
    }
    if (!data || data.items.length === 0) {
        return (
            <div className='bg-warning-subtle text-warning rounded-md px-4 py-3 text-sm'>
                표시할 정량지표가 없어요. 연동설정에서 평가를 연결하고 측정값을
                올려 주세요.
            </div>
        )
    }

    const { kpis, items } = data
    const noMeasurements = items.every((it) => !it.measuredAt)
    const plLabel: Record<string, string> = {
        high: '높음',
        medium: '보통',
        low: '낮음',
    }
    const adjusted = kpis.adjustedPassLikelihood
        ? (plLabel[kpis.adjustedPassLikelihood] ?? kpis.adjustedPassLikelihood)
        : '—'
    const kpiCards: [string, number | string][] = [
        ['모니터링 지표', kpis.indicators],
        ['기준 이탈', kpis.deviations],
        ['위험 평가항목', kpis.riskItems],
        ['합격 가능성(이탈 반영)', adjusted],
    ]

    return (
        <div className='grid gap-4'>
            {noMeasurements ? (
                <div className='border-border flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed p-4'>
                    <p className='text-muted-foreground text-sm'>
                        아직 측정값이 없어요. 데이터를 올리면 기준 대비 판정이
                        시작됩니다.
                    </p>
                    <Button
                        size='sm'
                        onClick={() =>
                            navigate(`/monitoring/${dashboardId}/settings`)
                        }>
                        <Plus className='size-4' />
                        데이터 적재
                    </Button>
                </div>
            ) : null}
            <div className='grid gap-3 sm:grid-cols-4'>
                {kpiCards.map(([label, value]) => (
                    <div
                        key={label}
                        className='border-border bg-card rounded-md border p-4'>
                        <p className='text-muted-foreground text-xs'>{label}</p>
                        <p className='text-foreground mt-1 text-xl font-bold'>
                            {value}
                        </p>
                    </div>
                ))}
            </div>

            <section className='border-border bg-card rounded-md border p-5'>
                <h2 className='text-foreground text-sm font-semibold'>
                    정량지표 준수 현황
                </h2>
                <p className='text-muted-foreground mt-0.5 mb-3 text-xs'>
                    체크리스트 기준(평가로직) 대비 최신 측정값으로 판정합니다.
                </p>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='text-muted-foreground border-border border-b text-xs'>
                                <th className='py-2 pr-3 text-left'>지표</th>
                                <th className='py-2 pr-3 text-left'>
                                    기준(평가로직)
                                </th>
                                <th className='py-2 pr-3 text-left'>
                                    최근 측정
                                </th>
                                <th className='py-2 pr-3 text-left'>판정</th>
                                <th className='py-2 text-left'>
                                    연결 평가항목
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr
                                    key={it.fieldId}
                                    onClick={() => openIndicator(it.fieldId)}
                                    className='border-border hover:bg-muted/50 cursor-pointer border-b align-top transition-colors last:border-0'>
                                    <td className='text-foreground py-2 pr-3'>
                                        {it.nameKo}
                                        {it.unit ? ` (${it.unit})` : ''}
                                    </td>
                                    <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                        {it.threshold ?? '—'}
                                    </td>
                                    <td className='py-2 pr-3'>
                                        {it.lastValue ?? (
                                            <span className='text-muted-foreground text-xs'>
                                                미측정
                                            </span>
                                        )}
                                    </td>
                                    <td className='py-2 pr-3'>
                                        <Badge
                                            tone={
                                                VERDICT_TONE[it.verdict] ??
                                                'neutral'
                                            }>
                                            {it.verdict}
                                        </Badge>
                                    </td>
                                    <td className='text-muted-foreground py-2 text-xs'>
                                        {it.itemTitle}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {selectedFieldId ? (
                <IndicatorModal
                    item={
                        items.find((it) => it.fieldId === selectedFieldId) ??
                        null
                    }
                    detail={
                        details?.find((d) => d.fieldId === selectedFieldId) ??
                        null
                    }
                    loading={detailsLoading}
                    onClose={() => setSelectedFieldId(null)}
                />
            ) : null}
        </div>
    )
}

/** 표 행 클릭 → 지표의 시계열·평가항목 상세를 모달로 보여준다. */
function IndicatorModal({
    item,
    detail,
    loading,
    onClose,
}: {
    item: { nameKo: string; verdict: string } | null
    detail: DeviationDetail | null
    loading: boolean
    onClose: () => void
}) {
    return (
        <Modal
            open
            onClose={onClose}
            className='max-w-3xl'
            title={
                <span className='flex min-w-0 items-center gap-2'>
                    <span className='truncate'>
                        {item?.nameKo ?? '지표 상세'}
                    </span>
                    {item ? (
                        <Badge tone={VERDICT_TONE[item.verdict] ?? 'neutral'}>
                            {item.verdict}
                        </Badge>
                    ) : null}
                </span>
            }>
            {loading ? (
                <p className='text-muted-foreground text-sm'>불러오는 중…</p>
            ) : detail ? (
                <IndicatorDetail detail={detail} />
            ) : (
                <p className='text-muted-foreground text-sm'>
                    이 지표의 상세를 불러오지 못했어요.
                </p>
            )}
        </Modal>
    )
}
